import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DatiqUtilsService } from '@dat/datiq-utils';
import {
    DatiqRulesConditionOperators,
    DatiqRulesRelations,
    IDatiqRulesCondition,
    IDatiqRulesData,
    IDatiqRulesRule,
} from '@dat/datiq-core/models';
import { DatiqRulesRegisterService } from './datiq-rules-register.service';
import { DatiqRulesService } from './datiq-rules.service';

@Injectable({
    providedIn: 'root',
})
export class DatiqRulesEvaluatorService {
    constructor(
        private _registration: DatiqRulesRegisterService,
        private _service: DatiqRulesService,
        private _utils: DatiqUtilsService,
    ) {}

    getPassedData$(rules: IDatiqRulesData<unknown>[]): Observable<unknown[]> {
        const ruleData$: Observable<IDatiqRulesData<unknown>>[] =
            this.mapRuleDataToEvaluators$(rules ?? []);

        return combineLatest(ruleData$).pipe(
            map((ruleCollection: IDatiqRulesData<unknown>[]): unknown[] =>
                ruleCollection
                    .filter(
                        (ruleData: IDatiqRulesData<unknown>): boolean =>
                            !!ruleData.passed,
                    )
                    .map((ruleData: IDatiqRulesData<unknown>): unknown => {
                        return ruleData.data;
                    }),
            ),
        );
    }

    allRulesPassed$(rules: IDatiqRulesData<unknown>[]): Observable<boolean> {
        const ruleData$: Observable<IDatiqRulesData<unknown>>[] =
            this.mapRuleDataToEvaluators$(rules ?? []);

        return !!rules?.length
            ? combineLatest(ruleData$).pipe(
                  map((ruleCollection: IDatiqRulesData<unknown>[]): boolean =>
                      ruleCollection.every(
                          (ruleData: IDatiqRulesData<unknown>): boolean =>
                              !!ruleData.passed,
                      ),
                  ),
              )
            : of(true);
    }

    /**
     * Converts rule data to rule observables that will say if something
     * is valid or not
     *
     * @param rules - rules to be validated
     * @returns observables that will evaluate the rule data
     */
    mapRuleDataToEvaluators$(
        rules: IDatiqRulesData<unknown>[],
    ): Observable<IDatiqRulesData<unknown>>[] {
        return (
            rules?.map?.(
                (
                    ruleData: IDatiqRulesData<unknown>,
                ): Observable<IDatiqRulesData<unknown>> =>
                    this.mapRuleDataToEvaluator$(ruleData),
            ) ?? []
        );
    }

    mapRuleDataToEvaluator$(
        ruleData: IDatiqRulesData<unknown>,
    ): Observable<IDatiqRulesData<unknown>> {
        const rules$: Observable<boolean> = this.evaluateRules$(
            ruleData.rules,
            ruleData.relation,
        );

        return rules$.pipe(
            map((passed: boolean) => ({
                ...ruleData,
                passed,
            })),
        );
    }

    evaluateRules$(
        rules: IDatiqRulesRule[],
        relation: DatiqRulesRelations = DatiqRulesRelations.All,
    ): Observable<boolean> {
        const ruleEvaluators$: Observable<boolean>[] = !!rules?.length
            ? rules?.map(
                  (rule: IDatiqRulesRule): Observable<boolean> =>
                      this.evaluateRule$(rule),
              )
            : [];

        return !!rules?.length
            ? combineLatest(ruleEvaluators$).pipe(
                  map((results: boolean[]): boolean =>
                      this.evaluateRuleResults(results, relation),
                  ),
              )
            : of(true);
    }

    evaluateRule$(rule: IDatiqRulesRule): Observable<boolean> {
        const conditionEvaluators$: Observable<boolean>[] =
            rule?.conditions?.map(
                (condition: IDatiqRulesCondition): Observable<boolean> => {
                    return this.evaluateCondition$(condition);
                },
            );

        return combineLatest(conditionEvaluators$).pipe(
            map((results: boolean[]): boolean =>
                this.evaluateConditionResults(results, rule.conditionOperator),
            ),
        );
    }

    evaluateCondition$(condition: IDatiqRulesCondition): Observable<boolean> {
        const type: string = condition.type as string;
       
        return !!this._registration.getEvaluator(type)?.evaluate$
            ? this._registration.getEvaluator(type).evaluate$(condition)
            : this._service.getNoEvaluator$(type);
    }

    evaluateRuleResults(
        results: boolean[],
        relation: DatiqRulesRelations,
    ): boolean {
        return this[`${relation}EvaluateResult`](results);
    }

    allEvaluateResult(results: boolean[]): boolean {
        return this._utils.allTrue(results);
    }

    someEvaluateResult(results: boolean[]): boolean {
        return this._utils.atLeastOneTrue(results);
    }
    noneEvaluateResult(results: boolean[]): boolean {
        return this._utils.allFalse(results);
    }

    evaluateConditionResults(
        results: boolean[],
        conditionOperator: DatiqRulesConditionOperators,
    ): boolean {
        return this[`${conditionOperator}EvaluateCondition`](results);
    }

    andEvaluateCondition(results: boolean[]): boolean {
        return this._utils.allTrue(results);
    }

    orEvaluateCondition(results: boolean[]): boolean {
        return this._utils.atLeastOneTrue(results);
    }

    notEvaluateCondition(results: boolean[]): boolean {
        return this._utils.allFalse(results);
    }
}
